import { useMemo } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { catalogApi } from '../api/endpoints/catalog';
import { useMyProfile } from './useMyProfile';
import { OTHER_PERMISSIONS_CATEGORY, PLATFORM_SCOPE_NAME } from '../lib/labelConstants';
import type { PermissionCatalogItem, Scope } from '../api/types/permissions';
import type { TenantLevelOption } from '../components/scoped/levelColumns';

export interface PermissionLabel {
    name: string;
    description?: string;
}

export interface LabelResolver {
    /** Nombre y descripción de un permiso; sin entrada en el catálogo, la clave como nombre. */
    permission(key: string): PermissionLabel;
    /** Nombre del alcance: plataforma, el tenant propio o el nivel de su aplicación; si no, el id o la clave. */
    scope(scope: Scope, scopeRef?: string): string;
    /** Categoría del permiso en el catálogo; fuera del catálogo o sin categoría, «Otros permisos». */
    category(key: string): string;
}

export interface LabelSources {
    permissions?: PermissionCatalogItem[];
    levels?: TenantLevelOption[];
    tenantId?: string;
    tenantDisplayName?: string;
}

function nonEmpty(value: string | undefined): string | undefined {
    return value && value.trim() ? value : undefined;
}

export function buildLabelResolver(sources: LabelSources): LabelResolver {
    const permissionByKey = new Map((sources.permissions ?? []).map((p) => [p.key, p]));
    const levelName = new Map((sources.levels ?? []).map((l) => [l.level, l.displayName]));
    const tenantName = nonEmpty(sources.tenantDisplayName);

    return {
        permission(key) {
            const item = permissionByKey.get(key);
            const name = nonEmpty(item?.displayName) ?? key;
            const description = nonEmpty(item?.description);
            return description ? { name, description } : { name };
        },
        scope(scope, scopeRef) {
            if (scope === 'PLATFORM') return PLATFORM_SCOPE_NAME;
            if (scope === 'TENANT') {
                const isOwnTenant = !scopeRef || scopeRef === sources.tenantId;
                return (isOwnTenant ? tenantName : undefined) ?? scopeRef ?? scope;
            }
            return nonEmpty(levelName.get(scope)) ?? scope;
        },
        category(key) {
            return (
                nonEmpty(permissionByKey.get(key)?.category)?.trim() ?? OTHER_PERMISSIONS_CATEGORY
            );
        },
    };
}

/** Catálogo de permisos del propio actor. */
export function ownCatalogQueryOptions() {
    return queryOptions({
        queryKey: ['catalog', 'permissions', 'self'],
        queryFn: () => catalogApi.listPermissions(),
        staleTime: 5 * 60_000,
    });
}

/** Resolvedor único de etiquetas de la consola: catálogo de permisos + niveles y nombre del perfil. */
export function useLabelResolver(): LabelResolver {
    const catalogQ = useQuery(ownCatalogQueryOptions());
    const profileQ = useMyProfile();

    const permissions = catalogQ.data?.items;
    const profile = profileQ.data;

    return useMemo(
        () =>
            buildLabelResolver({
                permissions,
                levels: profile?.levels,
                tenantId: profile?.tenantId,
                tenantDisplayName: profile?.tenantDisplayName,
            }),
        [permissions, profile],
    );
}
